import { Button } from 'antd';
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import { Link } from 'react-router-dom';

import { UserStatus } from '../../graphql.generated';
import routes from '../../paths.json';
import Exception from '../Exception';
import Loading from '../Loading';

import { UsersQuery, useUsersQuery } from './Users.generated';

type UsersState = {
  loading: boolean;
  users: UsersQuery['users'];
};

export const UsersContext = createContext<UsersState>({
  loading: false,
  users: [],
});

export const useUsers = (): UsersState['users'] =>
  useContext(UsersContext).users;

const UsersProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { data, loading, error } = useUsersQuery({
    variables: {
      filter: {
        status: UserStatus.Active,
      },
    },
  });

  const users = useMemo(() => data?.users || [], [data?.users]);

  if (error) {
    return (
      <Exception
        actions={
          <Link to={routes.home}>
            <Button type="primary">Back to Home</Button>
          </Link>
        }
        desc={error.message}
        title="Error"
        type={500}
      />
    );
  }

  // Initial loading
  if (!users && loading) {
    return <Loading />;
  }

  return (
    <UsersContext.Provider value={{ loading, users }}>
      {children}
    </UsersContext.Provider>
  );
};

UsersProvider.displayName = 'UsersProvider';

export default UsersProvider;
